// src/components/account/VehiclesTab.js
import React, { useState, useEffect } from 'react';
import { Settings, Car, Plus, Edit, Trash, AlertCircle } from 'lucide-react';
import { ref, onValue, remove, set, update } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const VehiclesTab = ({ userId, isMobile }) => {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [formError, setFormError] = useState('');
  const [actionResult, setActionResult] = useState({ type: '', message: '' });
  const [newVehicle, setNewVehicle] = useState({
    make: '',
    model: '',
    year: '',
    color: '',
    licensePlate: '',
    type: ''
  });
  
  // Fetch vehicles from Firebase
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const vehiclesRef = ref(database, `users/${userId}/vehicles`);
    
    const unsubscribe = onValue(vehiclesRef, (snapshot) => {
      try {
        if (snapshot.exists()) {
          const vehiclesData = snapshot.val();
          const vehiclesArray = Object.keys(vehiclesData).map(key => ({
            id: key,
            ...vehiclesData[key]
          }));
          
          // Sort vehicles: first by last used (if available), then by created date
          vehiclesArray.sort((a, b) => {
            // If both have lastUsed, sort by that first
            if (a.lastUsed && b.lastUsed) {
              return new Date(b.lastUsed) - new Date(a.lastUsed);
            }
            // If only one has lastUsed, prioritize that one
            if (a.lastUsed) return -1;
            if (b.lastUsed) return 1;
            // Otherwise sort by created date
            return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
          });
          
          setVehicles(vehiclesArray);
        } else {
          setVehicles([]);
        }
      } catch (error) {
        console.error('Error processing vehicles data:', error);
      } finally {
        setLoading(false);
      }
    }, (error) => {
      console.error('Error fetching vehicles:', error);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [userId]);
  
  // Reset form
  const resetForm = () => {
    setNewVehicle({
      make: '',
      model: '',
      year: '',
      color: '',
      licensePlate: '',
      type: ''
    });
    setFormError('');
    setEditingVehicle(null);
  };
  
  // Handle vehicle type selection
  const handleVehicleTypeSelect = (type) => {
    setNewVehicle({...newVehicle, type});
  };
  
  // Validate form
  const validateForm = () => {
    if (!newVehicle.make.trim()) {
      setFormError('Make is required');
      return false;
    }
    if (!newVehicle.model.trim()) {
      setFormError('Model is required');
      return false;
    }
    if (!newVehicle.year.trim()) {
      setFormError('Year is required');
      return false;
    }
    
    // Validate year is a number and reasonable (1900-current year + 1)
    const yearNum = parseInt(newVehicle.year);
    const currentYear = new Date().getFullYear();
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > currentYear + 1) {
      setFormError(`Year must be between 1900 and ${currentYear + 1}`);
      return false;
    }
    
    return true;
  };
  
  // Handle add/edit form submission
  const handleSubmitVehicle = async (e) => {
    e.preventDefault();
    
    if (!userId) return;
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      if (editingVehicle) {
        // Update existing vehicle
        const vehicleRef = ref(database, `users/${userId}/vehicles/${editingVehicle.id}`);
        await update(vehicleRef, {
          ...newVehicle,
          updatedAt: new Date().toISOString()
        });
        
        setActionResult({
          type: 'success',
          message: 'Vehicle updated successfully!'
        });
      } else {
        // Add new vehicle
        const vehicleId = `vehicle_${Date.now()}`;
        const vehicleRef = ref(database, `users/${userId}/vehicles/${vehicleId}`);
        
        await set(vehicleRef, {
          ...newVehicle,
          createdAt: new Date().toISOString()
        });
        
        setActionResult({
          type: 'success',
          message: 'Vehicle added successfully!'
        });
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionResult({ type: '', message: '' });
      }, 3000);
      
      // Reset form and hide it
      resetForm();
      setShowAddForm(false);
      
    } catch (error) {
      console.error('Error saving vehicle:', error);
      setActionResult({
        type: 'error',
        message: editingVehicle 
          ? 'Failed to update vehicle. Please try again.' 
          : 'Failed to add vehicle. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle edit vehicle
  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    setNewVehicle({
      make: vehicle.make || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      color: vehicle.color || '',
      licensePlate: vehicle.licensePlate || '',
      type: vehicle.type || ''
    });
    setShowAddForm(true);
    setFormError('');
  };
  
  // Handle delete vehicle
  const handleDeleteVehicle = async (vehicleId, vehicleName) => {
    // Use window.confirm instead of global confirm to avoid ESLint error
    if (!window.confirm(`Are you sure you want to delete ${vehicleName}?`)) return;
    
    try {
      setLoading(true);
      const vehicleRef = ref(database, `users/${userId}/vehicles/${vehicleId}`);
      await remove(vehicleRef);
      
      setActionResult({
        type: 'success',
        message: 'Vehicle deleted successfully!'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionResult({ type: '', message: '' });
      }, 3000);
      
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      setActionResult({
        type: 'error',
        message: 'Failed to delete vehicle. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Cancel add/edit form
  const handleCancelForm = () => {
    setShowAddForm(false);
    resetForm();
  };
  
  // Format vehicle display name
  const formatVehicleName = (vehicle) => {
    if (!vehicle) return '';
    return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  };
  
  // Vehicle type options
  const vehicleTypes = [
    { id: 'sedan', label: 'Sedan' },
    { id: 'suv', label: 'SUV' },
    { id: 'truck', label: 'Truck' },
    { id: 'van', label: 'Van' },
    { id: 'coupe', label: 'Coupe' },
    { id: 'convertible', label: 'Convertible' },
    { id: 'wagon', label: 'Wagon' },
    { id: 'other', label: 'Other' }
  ];
  
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">My Vehicles</h2>
        {!showAddForm && (
          <button 
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center"
            disabled={loading}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add New Vehicle
          </button>
        )}
      </div>
      
      {/* Action result message */}
      {actionResult.message && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${
          actionResult.type === 'success' 
            ? 'bg-green-50 text-green-600 border border-green-200' 
            : 'bg-red-50 text-red-600 border border-red-200'
        }`}>
          {actionResult.message}
        </div>
      )}
      
      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 border border-[#3A98B9]/20 rounded-lg p-5 bg-[#3A98B9]/5">
          <h3 className="font-medium mb-3">{editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</h3>
          
          {formError && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200 flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <p>{formError}</p>
            </div>
          )}
          
          <form onSubmit={handleSubmitVehicle}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. Toyota"
                  value={newVehicle.make}
                  onChange={(e) => setNewVehicle({...newVehicle, make: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. Camry"
                  value={newVehicle.model}
                  onChange={(e) => setNewVehicle({...newVehicle, model: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. 2020"
                  value={newVehicle.year}
                  onChange={(e) => setNewVehicle({...newVehicle, year: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="e.g. Blue"
                  value={newVehicle.color}
                  onChange={(e) => setNewVehicle({...newVehicle, color: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  License Plate
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Optional"
                  value={newVehicle.licensePlate}
                  onChange={(e) => setNewVehicle({...newVehicle, licensePlate: e.target.value})}
                />
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vehicle Type
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {vehicleTypes.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    className={`border rounded-md px-3 py-2 text-sm text-center transition-colors ${
                      newVehicle.type === type.id
                        ? 'border-[#3A98B9] bg-[#3A98B9]/10 text-[#3A98B9]'
                        : 'border-gray-300 hover:border-[#3A98B9]/50 hover:bg-[#3A98B9]/5'
                    }`}
                    onClick={() => handleVehicleTypeSelect(type.id)}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-4">
              <button
                type="button"
                onClick={handleCancelForm}
                disabled={loading}
                className="px-4 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className={`px-4 py-2 bg-[#3A98B9] text-white text-sm rounded-md hover:bg-[#2A87A8] flex items-center ${
                  loading ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingVehicle ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  editingVehicle ? 'Update Vehicle' : 'Save Vehicle'
                )}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Vehicle List */}
      {loading && vehicles.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3A98B9] mx-auto mb-2"></div>
          <p>Loading your vehicles...</p>
        </div>
      ) : vehicles.length > 0 ? (
        <div className="space-y-4">
          {vehicles.map(vehicle => (
            <div key={vehicle.id} className="border rounded-lg p-4 hover:border-[#3A98B9]/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-[#3A98B9]/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <Car className="h-5 w-5 text-[#3A98B9]" />
                  </div>
                  <div>
                    <p className="font-medium">{formatVehicleName(vehicle)}</p>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-500 mt-1">
                      {vehicle.color && <span>{vehicle.color}</span>}
                      {vehicle.licensePlate && <span>• {vehicle.licensePlate}</span>}
                      {vehicle.type && <span>• {vehicleTypes.find(t => t.id === vehicle.type)?.label || vehicle.type}</span>}
                      {vehicle.lastUsed && (
                        <span className="text-xs bg-[#3A98B9]/10 text-[#3A98B9] px-2 py-0.5 rounded-full">
                          Last used: {new Date(vehicle.lastUsed).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleEditVehicle(vehicle)}
                    className="text-[#3A98B9] hover:text-[#2A87A8] p-1"
                    title="Edit vehicle"
                    disabled={loading}
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => handleDeleteVehicle(vehicle.id, formatVehicleName(vehicle))}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Delete vehicle"
                    disabled={loading}
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#3A98B9]/10 flex items-center justify-center mb-4">
            <Settings className="h-8 w-8 text-[#3A98B9]" />
          </div>
          <h3 className="text-xl font-medium text-gray-800 mb-2">No vehicles added yet</h3>
          <p className="text-gray-600 max-w-md mx-auto mb-2">
            Adding your vehicle details helps us provide the best service for your specific make and model.
          </p>
          <p className="text-gray-600 max-w-md mx-auto mb-6">
            Complete information ensures we have the right supplies and equipment ready for your appointment.
          </p>
          <button 
            onClick={() => setShowAddForm(true)}
            className="bg-gradient-to-r from-[#3A98B9] to-[#2A87A8] text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-300"
            disabled={loading}
          >
            Add Your First Vehicle
          </button>
        </div>
      )}
      
      {/* Benefits info section */}
      {!loading && (
        <div className="mt-8 bg-[#3A98B9]/5 rounded-lg p-5 border border-[#3A98B9]/20">
          <h3 className="font-medium text-[#3A98B9] mb-2">Why add your vehicle information?</h3>
          <ul className="text-[#2A87A8] space-y-2">
            <li className="flex items-start">
              <div className="flex-shrink-0 h-5 w-5 rounded-full bg-[#3A98B9]/20 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-xs font-bold text-[#3A98B9]">✓</span>
              </div>
              Get customized detailing services for your specific make and model
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-5 w-5 rounded-full bg-[#3A98B9]/20 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-xs font-bold text-[#3A98B9]">✓</span>
              </div>
              Receive accurate pricing estimates based on your vehicle size and type
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-5 w-5 rounded-full bg-[#3A98B9]/20 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-xs font-bold text-[#3A98B9]">✓</span>
              </div>
              Keep a record of services performed on each of your vehicles
            </li>
            <li className="flex items-start">
              <div className="flex-shrink-0 h-5 w-5 rounded-full bg-[#3A98B9]/20 flex items-center justify-center mt-0.5 mr-2">
                <span className="text-xs font-bold text-[#3A98B9]">✓</span>
              </div>
              Book future appointments more quickly with saved vehicle information
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default VehiclesTab;